import { format } from "date-fns";
import type {
  DailyFocus,
  Decision,
  Task,
  Thought,
  WeeklyReview,
  WorkbenchBackup,
  WorkRecord
} from "../types";
import { formatShortDate, nowIso } from "./time";

export function downloadBlob(filename: string, content: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function exportJson(backup: WorkbenchBackup): void {
  downloadBlob(
    `创始人工作台备份-${format(new Date(), "yyyyMMdd-HHmm")}.json`,
    JSON.stringify(backup, null, 2),
    "application/json"
  );
}

function taskMarkdown(task: Task): string {
  return [
    `### ${task.title}`,
    `- 状态：${task.status}`,
    `- 领域：${task.area || "未分类"}`,
    `- 预期结果：${task.outcome || "未填写"}`,
    `- 下一步：${task.nextAction || "未填写"}`,
    `- 截止：${formatShortDate(task.dueAt)}`,
    task.waitingOn ? `- 等待：${task.waitingOn}` : "",
    task.body ? `\n${task.body}` : ""
  ]
    .filter(Boolean)
    .join("\n");
}

function thoughtMarkdown(thought: Thought): string {
  return [
    `### ${thought.title || "未命名思考"}`,
    `- 状态：${thought.status}`,
    thought.facts ? `- 事实：${thought.facts}` : "",
    thought.judgment ? `- 判断：${thought.judgment}` : "",
    thought.evidence ? `- 依据：${thought.evidence}` : "",
    thought.nextStep ? `- 下一步：${thought.nextStep}` : "",
    thought.reviewAt ? `- 复核：${formatShortDate(thought.reviewAt)}` : "",
    thought.body ? `\n${thought.body}` : ""
  ]
    .filter(Boolean)
    .join("\n");
}

function decisionMarkdown(decision: Decision): string {
  return [
    `### ${decision.title || "未命名决策"}`,
    `- 决策：${decision.decision}`,
    `- 理由：${decision.rationale}`,
    `- 假设：${decision.assumptions || "未填写"}`,
    `- 预期结果：${decision.expectedOutcome}`,
    `- 复核：${formatShortDate(decision.reviewAt)}`,
    decision.actualOutcome ? `- 实际结果：${decision.actualOutcome}` : ""
  ]
    .filter(Boolean)
    .join("\n");
}

export function exportMarkdown(records: WorkRecord[]): void {
  const active = records.filter((record) => !record.deletedAt);
  const tasks = active.filter((record): record is Task => record.kind === "task");
  const thoughts = active.filter((record): record is Thought => record.kind === "thought");
  const decisions = active.filter(
    (record): record is Decision => record.kind === "decision"
  );
  const focuses = active.filter(
    (record): record is DailyFocus => record.kind === "dailyFocus"
  );
  const reviews = active.filter(
    (record): record is WeeklyReview => record.kind === "weeklyReview"
  );
  const lines = [
    "# 态格创始人工作台导出",
    `导出时间：${new Date().toLocaleString("zh-CN")}`,
    "",
    "## 任务",
    "",
    ...tasks.map(taskMarkdown),
    "",
    "## 思考",
    "",
    ...thoughts.map(thoughtMarkdown),
    "",
    "## 决策",
    "",
    ...decisions.map(decisionMarkdown),
    "",
    "## 每日聚焦",
    "",
    ...focuses.map(
      (focus) =>
        `### ${focus.date}\n${focus.closeNote || "无收尾记录"}\n\n关联任务：${
          focus.taskIds.length
        } 项`
    ),
    "",
    "## 周复盘",
    "",
    ...reviews.map(
      (review) =>
        `### ${review.weekStart} 当周\n- 结果：${review.results}\n- 阻碍：${review.blockers}\n- 判断修正：${review.judgmentCorrection}\n- 暂不做：${review.notDoing}`
    )
  ];
  downloadBlob(
    `创始人工作台-${format(new Date(), "yyyyMMdd")}.md`,
    lines.join("\n"),
    "text/markdown;charset=utf-8"
  );
}

export function parseBackup(text: string): WorkbenchBackup {
  const parsed = JSON.parse(text) as WorkbenchBackup;
  if (parsed.schemaVersion !== 1 || !Array.isArray(parsed.records)) {
    throw new Error("备份文件格式不正确");
  }
  return parsed;
}

export interface CsvImportPreview {
  records: WorkRecord[];
  errors: string[];
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '"') {
      if (quoted && text[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === "," && !quoted) {
      row.push(cell);
      cell = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && text[index + 1] === "\n") index += 1;
      row.push(cell);
      if (row.some((value) => value.trim())) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }
  row.push(cell);
  if (row.some((value) => value.trim())) rows.push(row);
  return rows;
}

export function csvTemplate(): string {
  return [
    "类型,标题,正文,领域,预期结果,下一步,状态,截止日期,等待对象,跟进日期,判断,依据,复核日期",
    "待办,确认下周销售会主题,补充背景,销售,形成明确议程,与三位负责人确认,收件箱,2026-10-30,,,,,",
    "思考,供应链损耗为什么上升,,供应链,,,,,,,,先查批次数据,需核对三类损耗,2026-11-01"
  ].join("\n");
}

export function parseCsvImport(text: string): CsvImportPreview {
  const rows = parseCsvRows(text.replace(/^\uFEFF/, ""));
  if (rows.length < 2) return { records: [], errors: ["CSV 中没有可导入的数据"] };
  const headers = rows[0].map((header) => header.trim());
  const index = (name: string) => headers.indexOf(name);
  const get = (row: string[], name: string) => row[index(name)]?.trim() ?? "";
  const errors: string[] = [];
  const records: WorkRecord[] = [];
  const timestamp = nowIso();
  rows.slice(1).forEach((row, rowIndex) => {
    const importType = get(row, "类型");
    const title = get(row, "标题");
    if (!title) {
      errors.push(`第 ${rowIndex + 2} 行缺少标题`);
      return;
    }
    const base = {
      id: crypto.randomUUID(),
      title,
      body: get(row, "正文"),
      createdAt: timestamp,
      updatedAt: timestamp,
      revision: 0
    };
    if (importType === "待办" || importType === "任务") {
      const statusValue = get(row, "状态") || "inbox";
      records.push({
        ...base,
        kind: "task",
        importType,
        status: ["inbox", "next", "doing", "waiting", "done", "dropped"].includes(statusValue)
          ? (statusValue as Task["status"])
          : "inbox",
        area: get(row, "领域"),
        outcome: get(row, "预期结果"),
        nextAction: get(row, "下一步"),
        dueAt: get(row, "截止日期") || undefined,
        waitingOn: get(row, "等待对象") || undefined,
        followUpAt: get(row, "跟进日期") || undefined
      } as Task);
    } else if (importType === "思考") {
      records.push({
        ...base,
        kind: "thought",
        importType,
        status: "open",
        facts: "",
        judgment: get(row, "判断"),
        evidence: get(row, "依据"),
        nextStep: get(row, "下一步"),
        reviewAt: get(row, "复核日期") || undefined
      } as Thought);
    } else {
      errors.push(`第 ${rowIndex + 2} 行类型“${importType || "空"}”无法识别`);
    }
  });
  return { records, errors };
}
